import { useMemo, useRef } from "react";
import { Gauge, Info, ShieldAlert, TrendingUp, MapPin } from "lucide-react";
import { GridRankMap } from "@/components/GridRankMap";
import { extractCity } from "@/lib/address";
import { usePassed, useVisible } from "@/lib/useInView";
import { RadialScore } from "./primitives/RadialScore";
import { ComparisonBar } from "./primitives/ComparisonBar";
import { ScoreBenchmarkBar } from "./primitives/ScoreBenchmarkBar";
import { LockedFindings } from "./LockedFindings";
import KeyTakeaways from "./KeyTakeaways";
import KeywordVolumeLine from "./KeywordVolumeLine";
import PillarOverview from "./PillarOverview";
import LockedTiles from "./LockedTiles";
import PillarCompare from "./PillarCompare";
import ReviewsSummary from "./ReviewsSummary";
import Testimonials from "./Testimonials";
import AiTestCard from "./AiTestCard";
import OfferBlock, { FaqBlock } from "./OfferBlock";
import TrustBlock from "./TrustBlock";
import ReportCta from "./ReportCta";
import StickyOfferBar from "./StickyOfferBar";
import { TrackedSection } from "./TrackedSection";
import { T } from "./i18n";
import { S } from "./summaryCopy";
import { fallbackHooks, heroHeadline, noWebsiteInfo, competitorFacts } from "./summaryModel";
import { scoreTone, scoreBand, verdictKey, pickTopHooks, formatMeasuredOn } from "./reportUtils";

// The FREE report as one focused page instead of five tabs (proposal §B0): three
// of the five tabs were empty on this tier, and a prospect who reads for ninety
// seconds must reach the offer. Everything here is either a verifiable fact
// about THIS business or a plainly-labelled lock. The paid five-tab layout is a
// different component tree and is untouched.
export default function SummaryReport(props) {
  // A bare link without a measured score must not read like a verdict.
  if (props.data.digital_score == null) return <NotMeasured data={props.data} lang={props.lang} />;
  return <ScoredSummary {...props} />;
}

function NotMeasured({ data, lang }) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-8">
      <div className="rounded-2xl border bg-card p-6 shadow-sm sm:p-8">
        {data.name && <h1 className="font-heading text-xl font-bold">{data.name}</h1>}
        {data.address && <p className="mt-1 text-base text-muted-foreground">{data.address}</p>}
        <p className="mt-4 text-lg font-medium leading-snug">{S[lang].notMeasuredYet}</p>
      </div>
    </div>
  );
}

function ScoredSummary({ data, lang, token }) {
  const L = T[lang];
  const C = S[lang];
  const tone = scoreTone(data.digital_score);
  const band = scoreBand(data.digital_score, lang);
  const city = extractCity(data.address);
  const term = data.maps_rank && !data.maps_rank.error ? data.maps_rank.keyword || null : null;
  const headline = heroHeadline(data);
  const noSite = noWebsiteInfo(data);
  // Real hooks first; verifiable fallback observations top the list up to three.
  const hooks = useMemo(() => {
    // The missing website is shown as an open finding row; the hook would repeat it.
    const real = pickTopHooks(data, [
      ...(noSite?.inTeaser ? ["noWebsite"] : []),
      // The hero headline already names the rank; the hook would repeat the number.
      ...(headline.id === "mapsMid" ? ["mapsRank"] : []),
    ]);
    return [...real, ...fallbackHooks(data, 3 - real.length).map((h) => ({ ...h, fallback: true }))];
  }, [data, noSite?.inTeaser, headline.id]);
  // Fallback wording lives in S[lang].fallbackHooks; weakestPillar carries a raw
  // pillar key that must be translated first.
  const hookText = (h) => {
    if (!h.fallback) return C.hooks[h.id](...h.args);
    const args = (h.id === "weakestPillar" || h.id === "strongestPillar") ? [L.pillars[h.args[0]], ...h.args.slice(1)] : h.args;
    return C.fallbackHooks[h.id](...args);
  };
  const top3 = data.top3 || [];
  const top1 = top3[0] || null;
  const facts = competitorFacts(data);
  const gap = top1 ? Math.max(top1.score - data.digital_score, 0) : 0;
  // Ranked together with the competitors, not pinned first regardless of
  // score: "Sie" reading as the shortest bar at the bottom of a sorted list
  // is what actually makes "you're behind" legible at a glance, instead of
  // requiring the reader to compare four numbers by hand (owner feedback,
  // 2026-09-28 — the unsorted version buried the message the section exists
  // to make).
  const ranked = [{ label: L.you, score: data.digital_score, you: true }, ...top3.map((t) => ({ label: t.label, score: t.score, you: false }))]
    .sort((a, b) => b.score - a.score);
  const measuredOn = formatMeasuredOn(data.measured_at, lang);
  const median = data.benchmark?.score_median;
  // The score band is a fixed absolute threshold (verdictKey), unrelated to
  // the regional median just below it — for a low-ish absolute band ("Unter
  // Marktstandard" / "Kritische digitale Schwäche") that still sits above the
  // peer median, showing both at once reads as a straight contradiction
  // (owner-relayed review, 2026-09-28: score 53, median 42, badge said
  // "Unter Marktstandard" right next to "+11"). Swap the badge for the
  // truthful relative framing only in that specific case; everywhere else the
  // absolute band and the median comparison already agree.
  const bandKey = verdictKey(data.digital_score);
  const aboveMedianButLowBand = median != null && data.digital_score > median && (bandKey === "critical" || bandKey === "weak");
  const findingsTeaser = data.audit_findings_teaser || null;
  const findingCounts = data.finding_counts || null;
  const legalN = data.legal_teaser?.issue_count || 0;
  const grid = data.grid_rank && !data.grid_rank.error ? data.grid_rank : null;

  // Sticky mobile bar: appears once the first observations are behind the
  // reader, hides while the offer itself is on screen.
  const hooksEndRef = useRef(null);
  const offerRef = useRef(null);
  const passedHooks = usePassed(hooksEndRef);
  const offerVisible = useVisible(offerRef, 0.05);

  const scrollToOffer = () =>
    document.getElementById("angebot")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 pb-28 sm:py-8 sm:pb-8">
      {/* 1 — verdict. Three visual zones instead of one flat stack: the score
          reads instantly against its own grade color, the headline is the one
          loud element (a left accent bar in that same color, not a bigger
          font — the score ring is already the page's "big number"), and the
          peer comparison stays quiet underneath. tone.bg was already computed
          for this purpose everywhere else but never actually painted on
          anything (owner request 2026-09-27: make this card hit harder). */}
      <TrackedSection id="verdict" token={token}>
        <div className="overflow-hidden rounded-2xl border shadow-sm">
          <div className="flex flex-col items-center gap-5 p-6 sm:flex-row sm:p-8" style={{ background: tone.bg }}>
            <RadialScore value={data.digital_score} max={100} size={104} strokeWidth={11} color={tone.bar}>
              <span className="text-2xl font-bold tabular-nums" style={{ color: tone.fg }}>{data.digital_score}</span>
            </RadialScore>
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center gap-1.5 text-base font-medium text-muted-foreground sm:justify-start">
                <Gauge className="h-4 w-4" /> {C.scoreLabel}
              </div>
              <div
                className="mt-1.5 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold text-white"
                style={{ background: tone.bar }}
              >
                {aboveMedianButLowBand ? C.aboveMedianBadge(top1 && gap > 0 ? gap : null) : <>{band.emoji} {band.label}</>}
              </div>
            </div>
          </div>

          <div className="border-t bg-card p-6 sm:p-8">
            <div className="border-l-4 pl-4" style={{ borderColor: tone.bar }}>
              <p className="text-xl font-semibold leading-snug">
                {headline.id === "band"
                  ? C.verdictLine[verdictKey(data.digital_score)](city, term)
                  : C.headline[headline.id](...headline.args)}
              </p>
              {headline.id !== "band" && headline.id !== "mapsTopGridAll" && (
                <p className="mt-1.5 text-xs text-muted-foreground">{C.heroSource}</p>
              )}
            </div>

            {median != null && (
              <div className="mt-5 border-t pt-5">
                <p className="text-sm text-muted-foreground">{C.benchmarkLine(median, data.benchmark?.cohort_size)}</p>
                <ScoreBenchmarkBar value={data.digital_score} max={100} median={median} color={tone.bar} caption={C.benchmarkCaption} />
              </div>
            )}
            {measuredOn && (
              <p className="mt-4 text-sm text-muted-foreground">{L.measuredOn(measuredOn)} {L.verifyPromise}</p>
            )}
          </div>
        </div>
      </TrackedSection>

      {/* 1b — result at a glance + six areas */}
      <TrackedSection id="glance" token={token}>
        <KeyTakeaways data={data} lang={lang} token={token} headlineId={headline.id} showCta={data.purchase_status !== "paid"} />
      </TrackedSection>
      <TrackedSection id="pillars" token={token}>
        <PillarOverview data={data} lang={lang} />
      </TrackedSection>

      {/* 2 — you vs the three most visible, with names, plus the heatmap.
          Visibility first, deficiencies next (owner request 2026-09-27): a
          prospect should see where they stand before reading what's wrong. */}
      {top1 && (
        <TrackedSection id="compare" token={token} className="mt-6">
          <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
            <div className="border-b bg-secondary/50 p-5 sm:p-6">
              <h2 className="mb-1.5 flex items-center gap-1.5 font-heading text-lg font-semibold">
                <TrendingUp className="h-4 w-4" /> {C.compareTitle}
              </h2>
              <p className="text-base text-muted-foreground">{C.compareDesc}</p>
            </div>
            <div className="p-5 sm:p-6">
              {gap > 0 && (
                <div className="mb-5 flex items-center justify-center gap-4 border-b pb-5 text-center sm:gap-8">
                  <div>
                    <div className="text-3xl font-bold tabular-nums" style={{ color: tone.fg }}>{data.digital_score}</div>
                    <div className="text-sm text-muted-foreground">{L.yourValue}</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-sm font-medium text-muted-foreground">{L.gapLabel}</span>
                    <span className="text-2xl font-bold" style={{ color: tone.bar }}>−{gap}</span>
                  </div>
                  <div>
                    <div className="text-3xl font-bold tabular-nums text-emerald-600">{top1.score}</div>
                    <div className="text-sm text-muted-foreground">{top1.label}</div>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {ranked.map((r) => (
                  <ComparisonBar key={r.label} label={r.label} value={r.score} highlight={r.you} tone={tone} />
                ))}
              </div>
              {gap > 0 ? (
                <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-base text-amber-900">
                  {C.compareGap(top1.score, gap)}
                </p>
              ) : (
                <p className="mt-4 rounded-lg border border-green-200 bg-green-50 p-3 text-base text-green-900">{L.targetMet}</p>
              )}
              {facts.length > 0 && (
                <div data-testid="competitor-facts" className="mt-4">
                  <h3 className="text-sm font-medium text-muted-foreground">{C.competitorFacts.title(top1.label)}</h3>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-base">
                    {facts.map((f) => (
                      <li key={`${f.id}-${f.args[0]}`}>{C.competitorFacts[f.id](L.pillars[f.args[0]], ...f.args.slice(1))}</li>
                    ))}
                  </ul>
                </div>
              )}
              <PillarCompare data={data} lang={lang} />
            </div>
          </div>
        </TrackedSection>
      )}

      {/* 2a — primary CTA right after the comparison */}
      {top1 && data.purchase_status !== "paid" && (
        <div className="mt-5" data-testid="cta-after-compare">
          <ReportCta token={token} lang={lang} placement="compare" />
        </div>
      )}

      {/* 2b — the live AI answer: the most persuasive item, so it sits right after the comparison */}
      <TrackedSection id="aitest" token={token} className="mt-6">
        <AiTestCard token={token} lang={lang} initial={data.ai_test} businessName={data.name} top3={top3} />
      </TrackedSection>

      <KeywordVolumeLine keywordVolume={data.keyword_volume} lang={lang} />

      {/* 2c — heatmap: its own block, never gated on competitors */}
      {grid && (
        <TrackedSection id="heatmap" token={token} className="mt-6">
          <div className="rounded-2xl border bg-card p-5 shadow-sm sm:p-6">
            <h2 className="mb-1.5 flex items-center gap-1.5 font-heading text-lg font-semibold">
              <MapPin className="h-4 w-4" /> {L.heatmapTitle}
            </h2>
            <p className="mb-3 text-base text-muted-foreground">{L.heatmapDesc(grid.size, grid.keyword)}</p>
            <GridRankMap gridRank={grid} lang={lang} />
          </div>
        </TrackedSection>
      )}

      {/* 3 — three things, fully open, each checkable by the reader */}
      {hooks.length > 0 && (
        <TrackedSection id="hooks" token={token} className="mt-6">
          <h2 className="font-heading text-xl font-bold">{hooks.length === 3 ? C.topFindingsTitle : C.topFindingsTitleFew}</h2>
          <p className="mt-1 text-base text-muted-foreground">{C.topFindingsDesc}</p>
          <ol className="mt-4 space-y-3">
            {hooks.map((h, i) => (
              <li key={h.id} className="rounded-xl border bg-card p-4 shadow-sm">
                <div className="flex gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-base">{hookText(h)}</p>
                    {!h.fallback && (
                      <p className="mt-1.5 text-sm text-muted-foreground">
                        <span className="font-semibold">{C.selfCheckLabel}</span> {C.hookChecks[h.id](...h.checkArgs)}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </TrackedSection>
      )}
      <div ref={hooksEndRef} aria-hidden="true" />

      {/* 4b — what the paid report contains, as lock cards */}
      <TrackedSection id="tiles" token={token}>
        <LockedTiles data={data} lang={lang} />
      </TrackedSection>

      {/* 5 — every finding TITLE (locked) + one finding in full */}
      {findingsTeaser?.length > 0 && findingCounts?.total > 0 && (
        <TrackedSection id="locked" token={token} className="mt-6">
          <LockedFindings
            teaser={findingsTeaser}
            counts={findingCounts}
            sample={data.sample_finding}
            L={L}
            onCtaClick={scrollToOffer}
          />
        </TrackedSection>
      )}

      {/* 6 — legal: a count and a disclaimer, never the titles (self-diagnosable in seconds) */}
      {legalN > 0 && (
        <TrackedSection id="legal" token={token} className="mt-6">
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 shadow-sm sm:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-amber-900">
              <ShieldAlert className="h-5 w-5" /> {C.legalCardTitle(legalN)}
            </h2>
            <p className="mt-2 text-base text-amber-900/90">{C.legalIntro}</p>
            <p className="mt-2 text-base text-amber-900/90">
              {L.legalTeaser(legalN, !!data.legal_teaser.has_image_license_flag)}
            </p>
            <p className="mt-3 flex items-start gap-2 rounded-lg bg-white/60 p-3 text-sm text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span>{L.legalDisclaimer}</span>
            </p>
          </div>
        </TrackedSection>
      )}

      {/* 7 — compact reviews: rating, count, regional comparison; never single reviews */}
      {data.review_count != null && (
        <TrackedSection id="reviews" token={token} className="mt-6">
          <ReviewsSummary data={data} lang={lang} />
        </TrackedSection>
      )}

      {/* 7b — real customer quotes, right before the offer (owner request 2026-09-30) */}
      <Testimonials lang={lang} />

      {/* 8 — the offer, FAQ, sender */}
      <TrackedSection id="offer" token={token} className="mt-8">
        <div ref={offerRef}>
          <OfferBlock token={token} lang={lang} findingTotal={findingCounts?.total || 0} purchaseStatus={data.purchase_status} />
        </div>
      </TrackedSection>
      {noSite && (
        <TrackedSection id="rebuild" token={token} className="mt-6">
          <div className="rounded-2xl border bg-card p-5 shadow-sm sm:p-6 print:hidden">
            <h2 className="font-heading text-lg font-semibold">{C.noSite.cardTitle}</h2>
            <p className="mt-1 text-base text-muted-foreground">{C.noSite.cardBody}</p>
            <a href="/angebot" className="mt-4 inline-flex items-center rounded-xl border border-primary px-5 py-2.5 text-base font-semibold text-primary transition hover:bg-primary/10">
              {C.noSite.cardCta}
            </a>
          </div>
        </TrackedSection>
      )}
      <TrackedSection id="faq" token={token} className="mt-8">
        <h2 className="font-heading text-xl font-bold">{C.faqTitle}</h2>
        <FaqBlock lang={lang} />
      </TrackedSection>
      <TrustBlock lang={lang} />

      <StickyOfferBar token={token} lang={lang} show={passedHooks && !offerVisible && data.purchase_status !== "paid"} />
    </div>
  );
}
